import React, { useCallback, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import CustomLink from "../../atoms/CustomLink/CustomLink.atom";
import Button from "../../atoms/Button/Button.component";
import Close from "../../../assets/svgs/Close.svg";
import ContactCard, { ContactCardProps } from "../ContactCardSection/ContactCard.blok";
import { ButtonProps } from "../../../interfaces/Button.interface";
import { StoryblokAsset } from "../../../interfaces/Storyblok/StoryblokAsset.interface";
import { ButtonTypes } from "../../../interfaces/enums/ButtonTypes.enum";
import { ButtonSizeTypes } from "../../../interfaces/enums/ButtonSizeTypes.enum";
import {
  StickyButtonIconPosition,
  StickyButtonPosition,
  StoryblokStickyButton,
} from "../../../interfaces/Storyblok/StoryblokStickyButton.interface";
import getCurrentUserState from "../../../utils/getCurrentUserState/getCurrentUserState.util";
import matchLocationToState, {
  AustralianStateCode,
} from "../../../utils/getCurrentUserState/matchLocationToState.util";

// Kept in step with the duration-300 classes below so the panel unmounts only once it has slid out.
const PANEL_TRANSITION_MS = 300;

interface PositionConfig {
  container: string;
  rounded: string;
  // Anchors the contact card panel to the same corner as the tab, clear of the tab itself.
  panel: string;
  // Where the panel rests while closed, so it slides out from behind the tab.
  panelClosed: string;
  // When true the label reads bottom-to-top (edge on the right); otherwise top-to-bottom (edge on the left).
  readUpwards: boolean;
}

const positionConfig: Record<StickyButtonPosition, PositionConfig> = {
  "top-left": {
    container: "top-24 left-0",
    rounded: "rounded-r-[24px]",
    panel: "sm:left-[58px] sm:top-24",
    panelClosed: "-translate-x-full",
    readUpwards: false,
  },
  "top-right": {
    container: "top-24 right-0",
    rounded: "rounded-l-[24px]",
    panel: "sm:right-[58px] sm:top-24",
    panelClosed: "translate-x-full",
    readUpwards: true,
  },
  "bottom-left": {
    container: "bottom-24 left-0",
    rounded: "rounded-r-[24px]",
    panel: "sm:left-[58px] sm:bottom-24",
    panelClosed: "-translate-x-full",
    readUpwards: false,
  },
  "bottom-right": {
    container: "bottom-24 right-0",
    rounded: "rounded-l-[24px]",
    panel: "sm:right-[58px] sm:bottom-24",
    panelClosed: "translate-x-full",
    readUpwards: true,
  },
};

const EnquiryIcon: React.FC<{ flip?: boolean }> = ({ flip }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
    className={classNames("shrink-0", { "-scale-x-100": flip })}
  >
    <path
      d="M23.5312 15.6094L23.7187 15.8906C23.9062 16.125 24 16.4062 24 16.6875C24 17.3906 23.4375 18 22.6875 18L19.5 18L19.5 19.5C19.5 21.9844 17.4844 24 15 24L4.5 24C2.01562 24 -2.1924e-07 21.9844 -3.27835e-07 19.5L-9.83506e-07 4.5C-1.0921e-06 2.01562 2.01562 -2.84807e-07 4.5 -3.93402e-07L15 -8.52372e-07C17.4844 -9.60968e-07 19.5 2.01562 19.5 4.5L19.5 10.125C19.5 10.2187 19.5469 10.2656 19.5937 10.3594L23.5312 15.6094ZM19.5 16.5L22.3125 16.5L18.375 11.25C18.1406 10.9219 18 10.5469 18 10.125L18 4.5C18 2.85937 16.6406 1.5 15 1.5L4.5 1.5C2.85937 1.5 1.5 2.85937 1.5 4.5L1.5 19.5C1.5 21.1406 2.85937 22.5 4.5 22.5L15 22.5C16.6406 22.5 18 21.1406 18 19.5L18 17.625C18 17.0156 18.5156 16.5 19.125 16.5L19.5 16.5ZM9 18.75L9 11.25C9 10.8281 9.32812 10.5 9.75 10.5C10.1719 10.5 10.5 10.8281 10.5 11.25L10.5 18.75C10.5 19.1719 10.1719 19.5 9.75 19.5C9.32812 19.5 9 19.1719 9 18.75ZM9 7.5L9 5.25C9 4.82812 9.32812 4.5 9.75 4.5C10.1719 4.5 10.5 4.82812 10.5 5.25L10.5 7.5C10.5 7.92187 10.1719 8.25 9.75 8.25C9.32812 8.25 9 7.92187 9 7.5ZM13.5 18.75L13.5 15C13.5 14.5781 13.8281 14.25 14.25 14.25C14.6719 14.25 15 14.5781 15 15L15 18.75C15 19.1719 14.6719 19.5 14.25 19.5C13.8281 19.5 13.5 19.1719 13.5 18.75ZM13.5 11.25L13.5 5.25C13.5 4.82812 13.8281 4.5 14.25 4.5C14.6719 4.5 15 4.82812 15 5.25L15 11.25C15 11.6719 14.6719 12 14.25 12C13.8281 12 13.5 11.6719 13.5 11.25Z"
      fill="currentColor"
    />
  </svg>
);

/*-- Falls back to the first card whenever the state is unknown or nothing matches it. --*/
const getCardForState = (
  contactCards: ContactCardProps[],
  state: AustralianStateCode | undefined,
): ContactCardProps | undefined => {
  const matchedCard = state
    ? contactCards.find((card: ContactCardProps) => matchLocationToState(card.location, state))
    : undefined;

  return matchedCard ?? contactCards[0];
};

const hasLabel = (buttons: ButtonProps[]): boolean =>
  Array.isArray(buttons) && buttons.length > 0 && !!buttons[0].label;

/*-- An Asset field arrives as an object, so reading it as a string put "[object Object]" in the src
     and broke the image. Accepts either shape, and works the same for SVG, PNG and JPEG. --*/
const getIconUrl = (icon: StoryblokAsset | string | undefined): string | undefined => {
  const filename = typeof icon === "string" ? icon : icon?.filename;

  return filename?.trim() || undefined;
};

const StickyButton: React.FC<StoryblokStickyButton> = ({
  sticky_button,
  position,
  override_click,
  contact_cards = [],
  custom_icon,
  icon_position,
  custom_color,
  contact_card_redirect_link,
  contact_card_link_label,
}) => {
  // isPanelOpen is the intent, isPanelMounted keeps the panel in the DOM until it has slid out,
  // and isPanelVisible drives the transition classes one frame after mounting.
  const [isPanelOpen, setIsPanelOpen] = useState<boolean>(false);
  const [isPanelMounted, setIsPanelMounted] = useState<boolean>(false);
  const [isPanelVisible, setIsPanelVisible] = useState<boolean>(false);
  const [isLocating, setIsLocating] = useState<boolean>(false);
  const [userState, setUserState] = useState<AustralianStateCode | undefined>(undefined);
  // Remembers that detection has already run so re-opening the panel never re-prompts.
  const hasResolvedStateRef = useRef<boolean>(false);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const closePanel = useCallback(() => setIsPanelOpen(false), []);

  useEffect(() => {
    if (isPanelOpen) {
      setIsPanelMounted(true);
      return undefined;
    }

    const timer = window.setTimeout(() => setIsPanelMounted(false), PANEL_TRANSITION_MS);

    return () => window.clearTimeout(timer);
  }, [isPanelOpen]);

  useEffect(() => {
    if (!isPanelMounted || !isPanelOpen) {
      setIsPanelVisible(false);
      return undefined;
    }

    const frame = requestAnimationFrame(() => {
      setIsPanelVisible(true);
      closeButtonRef.current?.focus();
    });

    return () => cancelAnimationFrame(frame);
  }, [isPanelMounted, isPanelOpen]);

  useEffect(() => {
    if (!isPanelOpen) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closePanel();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isPanelOpen, closePanel]);

  if (!hasLabel(sticky_button)) {
    return null;
  }

  const button = sticky_button[0];
  const selectedPosition: StickyButtonPosition = (Array.isArray(position) ? position[0] : position) ?? "bottom-right";
  const config = positionConfig[selectedPosition] ?? positionConfig["bottom-right"];

  const cachedUrl = typeof button.link === "string" ? button.link : button.link?.cached_url;
  const anchor = typeof button.link === "object" ? button.link?.anchor : undefined;
  const linkType = typeof button.link === "object" ? button.link?.linktype : undefined;

  const customColor = custom_color?.trim() ? custom_color.trim() : "#ED3E42";

  const isOverridden = !!override_click && contact_cards.length > 0;

  // Resolved the same way as the tab's own link so the CTA handles internal paths,
  // same-page anchors and outbound URLs identically.
  const redirectUrl = (
    typeof contact_card_redirect_link === "string" ? contact_card_redirect_link : contact_card_redirect_link?.cached_url
  )?.trim();
  const redirectLabel = contact_card_link_label?.trim();

  if (!isOverridden && !cachedUrl) {
    return null;
  }

  const tabClassName = classNames(
    "group flex flex-col items-center gap-[10px] px-[9px] py-[21px] text-White shadow-2xl transition-all duration-300 ease-in-out hover:brightness-95 focus:outline-none focus:ring-0",
    config.rounded,
  );

  // Applied inline because Tailwind can only generate arbitrary values it sees at build time,
  // so an authored colour can never come through as a bg-[...] class.
  const tabStyle: React.CSSProperties = { backgroundColor: customColor };

  const customIconUrl = getIconUrl(custom_icon);
  const selectedIconPosition: StickyButtonIconPosition =
    (Array.isArray(icon_position) ? icon_position[0] : icon_position) ?? "leading";

  const labelContent = (
    <span
      className="font-extrabold uppercase tracking-[0.25em] text-sm leading-none"
      style={{
        writingMode: "vertical-rl",
        transform: config.readUpwards ? "rotate(180deg)" : undefined,
      }}
    >
      {button.label}
    </span>
  );

  const iconContent = customIconUrl ? (
    <img
      src={customIconUrl}
      // Decorative: the tab already takes its accessible name from the visible label.
      alt=""
      aria-hidden="true"
      // vertical-rl lays the label on its side and readUpwards adds the 180deg that makes it read
      // bottom-to-top. Matching both angles lets an upright icon lie the same way as the letters,
      // where the old flat 180deg left it standing on its head.
      style={{ transform: `rotate(${config.readUpwards ? 270 : 90}deg)` }}
      // object-contain keeps a non-square PNG or JPEG in proportion instead of stretching it.
      className="h-6 w-6 shrink-0 object-contain"
    />
  ) : (
    <EnquiryIcon flip={!config.readUpwards} />
  );

  // The column stacks top-to-bottom, but the label reads bottom-to-top on right-hand tabs, so there
  // the leading icon has to come last in the DOM to end up ahead of the words.
  const isIconFirst = config.readUpwards ? selectedIconPosition === "trailing" : selectedIconPosition === "leading";

  const tabContent = (
    <>
      {isIconFirst && iconContent}
      {labelContent}
      {!isIconFirst && iconContent}
    </>
  );

  const openPanel = () => {
    setIsPanelOpen(true);

    if (hasResolvedStateRef.current) {
      return;
    }

    setIsLocating(true);
    getCurrentUserState().then((state: AustralianStateCode | undefined) => {
      hasResolvedStateRef.current = true;
      setUserState(state);
      setIsLocating(false);
    });
  };

  const activeCard = getCardForState(contact_cards, userState);

  return (
    <>
      <div className={classNames("fixed z-50", config.container)}>
        {isOverridden ? (
          <button
            type="button"
            onClick={openPanel}
            className={tabClassName}
            style={tabStyle}
            aria-expanded={isPanelOpen}
          >
            {tabContent}
          </button>
        ) : (
          <CustomLink
            url={`${cachedUrl ?? ""}`}
            anchor={typeof anchor === "string" ? anchor : ""}
            linkType={linkType}
            className={tabClassName}
            style={tabStyle}
          >
            {tabContent}
          </CustomLink>
        )}
      </div>

      {isOverridden && isPanelMounted && (
        <div
          className={classNames(
            "fixed inset-0 z-[60] flex items-center justify-center overflow-hidden bg-Opacity5 p-4 transition-opacity duration-300 ease-out motion-reduce:transition-none sm:block sm:p-0",
            // Stops the backdrop swallowing clicks during the 300ms slide-out.
            isPanelVisible ? "opacity-100" : "pointer-events-none opacity-0",
          )}
          onClick={closePanel}
          role="presentation"
        >
          <div
            className={classNames(
              "relative w-full max-w-[402.5px] transition-transform duration-300 ease-out motion-reduce:transition-none sm:absolute",
              config.panel,
              isPanelVisible ? "translate-x-0" : config.panelClosed,
            )}
            onClick={(event: React.MouseEvent<HTMLDivElement>) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={button.label}
          >
            <button
              ref={closeButtonRef}
              type="button"
              onClick={closePanel}
              aria-label="Close"
              className="absolute -top-11 right-0 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-White text-Primary-900 shadow-lg transition-transform duration-300 hover:scale-110"
            >
              <Close className="h-3.5 w-3.5" />
            </button>

            {isLocating ? (
              <div className="flex w-full items-center gap-4 rounded-[24px] bg-White p-6 shadow-lg">
                <span
                  aria-hidden="true"
                  className="h-6 w-6 shrink-0 animate-spin rounded-full border-2 border-Primary-200 border-t-SP6"
                />
                <p className="text-[15px] font-normal leading-[20px] text-Body">Finding your closest contact&hellip;</p>
              </div>
            ) : (
              !!activeCard && (
                <ul className="w-full">
                  <ContactCard key={activeCard._uid} {...activeCard} />
                </ul>
              )
            )}

            {!isLocating && !!redirectUrl && !!redirectLabel && (
              <div className="mt-6 flex w-full justify-center">
                <Button
                  link={contact_card_redirect_link}
                  label={redirectLabel}
                  type={ButtonTypes.PRIMARY}
                  size={ButtonSizeTypes.LG}
                  // Closing first releases the scroll lock, so a same-page anchor can actually scroll to its target.
                  onClick={closePanel}
                  fullWidth
                  wrap
                />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default StickyButton;
